import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";

/**
 * Espace compte minimal (VVS_MODELE_INTERFACE.md §10, gabarit G5 : menu
 * latéral 3 col + contenu 9 col dès 1024px, empilé en dessous) — port de
 * apps/web/app/(account)/compte/page.tsx. Ce lot ne couvre que le tableau de
 * bord (identité + déconnexion) : les autres rubriques du §10.2 (adresses,
 * alertes, avis, fidélité, informations) n'ont pas d'endpoint apps/api
 * correspondant pour l'instant et ne sont donc pas rendues comme des liens
 * actifs, pour éviter un écran cassé — elles apparaissent en aperçu, non
 * cliquables (identique à l'original).
 *
 * Pas de session à vérifier ici : `AccountLayout` (via `AccountRouteGuard`)
 * a déjà redirigé vers `/connexion` si `useCustomerAuth().isLoggedIn` est
 * faux avant que cette page ne soit montée — contrairement à l'original, où
 * chaque page `(account)/compte/*` refaisait individuellement
 * `getAuthSession()` + `redirect()` faute de garde au niveau layout.
 */
export function ComptePage() {
  const { user, logout } = useCustomerAuth();
  const navigate = useNavigate();

  // Filet de sécurité pour le typage (`user` est `CustomerAuthUser | null`) :
  // ne devrait jamais se produire en pratique, `AccountRouteGuard` garantit
  // déjà `isLoggedIn === true` (donc `user !== null`) avant le montage de
  // cette page.
  if (!user) return null;

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(" ");

  function handleLogout() {
    // Pas d'endpoint serveur de déconnexion aujourd'hui (cf. commentaire de
    // tête de customer-auth-context.tsx `logout()`) : on efface seulement
    // l'état UI local, comme apps/web (`logoutAction`) le fait déjà.
    logout();
    navigate("/");
  }

  return (
    <div className="container-vvs py-8 lg:py-12">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="rounded-sm border border-line bg-white p-5">
            <p className="text-sm text-muted">Bonjour,</p>
            <p className="font-display text-lg font-semibold text-navy">{displayName || "Client VVS"}</p>
            <p className="mt-1 text-sm text-muted">{maskPhone(user.phoneE164)}</p>
          </div>

          <nav aria-label="Menu du compte" className="mt-4 rounded-sm border border-line bg-white">
            <ul className="divide-y divide-line text-sm">
              <li className="px-4 py-3 font-medium text-navy">Tableau de bord</li>
              <li>
                <Link to="/compte/commandes" className="block px-4 py-3 text-ink hover:bg-cream2 hover:text-navy">
                  Mes commandes
                </Link>
              </li>
              <li>
                <Link to="/compte/favoris" className="block px-4 py-3 text-ink hover:bg-cream2 hover:text-navy">
                  Mes favoris
                </Link>
              </li>
              {["Mes adresses", "Mes alertes stock", "Mes avis", "Fidélité & parrainage", "Mes informations"].map(
                (label) => (
                  <li key={label} className="px-4 py-3 text-muted" title="Bientôt disponible">
                    {label}
                    <span className="ml-2 text-xs uppercase tracking-wide text-line">Bientôt</span>
                  </li>
                ),
              )}
            </ul>
            <div className="border-t border-line">
              <button
                type="button"
                onClick={handleLogout}
                className="w-full px-4 py-3 text-left text-sm font-medium text-navy hover:bg-cream2"
              >
                Se déconnecter
              </button>
            </div>
          </nav>
        </aside>

        <div className="lg:col-span-9">
          <h1 className="font-display text-2xl font-bold text-navy">Tableau de bord</h1>
          <p className="mt-2 text-sm text-muted">Vous êtes connecté avec le numéro {maskPhone(user.phoneE164)}.</p>

          <div className="mt-6 rounded-sm border border-line bg-white p-6">
            <h2 className="font-display text-lg font-semibold text-navy">Mes informations</h2>
            <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">Téléphone</dt>
                <dd className="text-sm text-ink">{user.phoneE164}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">Prénom</dt>
                <dd className="text-sm text-ink">{user.firstName || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">Nom</dt>
                <dd className="text-sm text-ink">{user.lastName || "—"}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}

/** "+2250700000000" → "+2250700 ** ** 00" (reprend l'esprit du masquage de VVS_MODELE_INTERFACE.md §10.2). */
function maskPhone(phoneE164: string): string {
  if (phoneE164.length < 6) return phoneE164;
  const visibleStart = phoneE164.slice(0, phoneE164.length - 6);
  const last = phoneE164.slice(-2);
  return `${visibleStart} ** ** ${last}`;
}
